import React, { useEffect, useState, useRef, useCallback } from "react";
import { BASE_APIURL } from "utils/helpers";
import { useNavigate } from "react-router-dom";
import LoaderSpiner from "Components/LoaderSpiner";
import toast, { Toaster } from "react-hot-toast";
import { Trash2, Edit, Eye } from "lucide-react";

const BlogPost = () => {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [continuationToken, setContinuationToken] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const [previewBlog, setPreviewBlog] = useState(null);
  const [deletingBlogKey, setDeletingBlogKey] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);

  const [editingBlog, setEditingBlog] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    category: "",
    customCategory: "",
    thumbnail: "",
    content: "",
  });

  const navigate = useNavigate();
  const observerRef = useRef(null);
  const limit = 2;

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const res = await fetch(`${BASE_APIURL}/api/blogs/categories`, {
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.categories)) {
        setCategories(["All", ...data.categories]); // ✅ Only real categories
      } else {
        setCategories(["All"]);
      }
    } catch (err) {
      console.error("❌ Category fetch error:", err);
      setCategories(["All"]);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch blogs
  const fetchBlogs = async (reset = false) => {
    setLoading(true);
    try {
      let url =
        activeCategory === "All"
          ? `${BASE_APIURL}/api/blogs/all-posts?limit=${limit}`
          : `${BASE_APIURL}/api/blogs/posts/category/${encodeURIComponent(
              activeCategory
            )}?limit=${limit}`;

      if (continuationToken && !reset) {
        url += `&continuationToken=${encodeURIComponent(continuationToken)}`;
      }

      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch blogs");
      const data = await res.json();

      if (reset) setBlogs(data.posts || []);
      else setBlogs((prev) => [...prev, ...(data.posts || [])]);

      setHasMore(Boolean(data.hasMore));
      setContinuationToken(data.nextContinuationToken || null);
    } catch (err) {
      setError("Failed to load blogs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setBlogs([]);
    setContinuationToken(null);
    setHasMore(true);
    fetchBlogs(true);
  }, [activeCategory]);

  // Infinite scroll
  const lastBlogRef = useCallback(
    (node) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) fetchBlogs(false);
      });

      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore, activeCategory]
  );

  // Delete blog
  const handleDelete = async (blog) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) return;
    setDeletingBlogKey(blog.key);
    try {
      const res = await fetch(
        `${BASE_APIURL}/api/blogs/post/${encodeURIComponent(blog.key)}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error("Failed to delete blog");
      setBlogs((prev) => prev.filter((b) => b.key !== blog.key));
      toast.success("Blog deleted successfully");
    } catch (err) {
      toast.error("❌ Error deleting blog");
    } finally {
      setDeletingBlogKey(null);
    }
  };

  // Delete category
  const handleDeleteCategory = async (category) => {
    if (category === "All") {
      toast.error("You cannot delete the 'All' category");
      return;
    }
    if (!window.confirm(`Delete category "${category}" and all its blogs?`))
      return;

    setDeletingCategory(category);
    try {
      const res = await fetch(
        `${BASE_APIURL}/api/blogs/categories/${encodeURIComponent(category)}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error("Failed to delete category");
      setCategories((prev) => prev.filter((c) => c !== category));
      if (activeCategory === category) setActiveCategory("All");
      toast.success("Category deleted successfully");
    } catch (err) {
      toast.error("❌ Error deleting category");
    } finally {
      setDeletingCategory(null);
    }
  };

  // Edit modal functions
  const openEditModal = (blog) => {
    const isExistingCategory = categories.includes(blog.category);

    setEditingBlog(blog);
    setFormData({
      title: blog.title || "",
      category: isExistingCategory ? blog.category : "Other",
      customCategory: "",
      thumbnail: blog.thumbnail || "",
      content: blog.content || "",
    });
  };

  const closeEditModal = () => {
    setEditingBlog(null);
    setFormData({
      title: "",
      category: "",
      customCategory: "",
      thumbnail: "",
      content: "",
    });
  };

  const handleUpdateBlog = async () => {
    setEditLoading(true);
    try {
      const updatedBlog = {
        title: formData.title,
        category:
          formData.category === "Other"
            ? formData.customCategory
            : formData.category,
        thumbnail: formData.thumbnail,
        content: formData.content,
      };

      const res = await fetch(
        `${BASE_APIURL}/api/blogs/post/${encodeURIComponent(editingBlog.key)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedBlog),
        }
      );

      if (!res.ok) throw new Error("Failed to update blog");

      setBlogs((prev) =>
        prev.map((b) =>
          b.key === editingBlog.key
            ? { ...b, ...updatedBlog, updatedAt: new Date().toISOString() }
            : b
        )
      );

      toast.success("Blog updated successfully");
      closeEditModal();
    } catch (err) {
      console.error(err);
      toast.error("Error updating blog");
    } finally {
      setEditLoading(false);
    }
  };

  if (loading && blogs.length === 0) return <LoaderSpiner />;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className="w-full p-8 mx-auto max-w-7xl">
      <h2 className="mb-6 text-3xl font-bold">Manage Blogs</h2>

      {/* Categories */}
      {/* <div className="flex flex-wrap gap-4 mb-6">
        {categories.map((category, i) => (
          <div key={i} className="flex items-center gap-2">
            <button
              onClick={() => setActiveCategory(category)}
              className={`px-3 py-1 rounded font-medium ${
                activeCategory === category
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-800"
              }`}
            >
              {category}
            </button>
            {category !== "All" && (
              <button
                onClick={() => handleDeleteCategory(category)}
                disabled={deletingCategory === category}
                className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
              >
                {deletingCategory === category ? (
                  <span className="inline-block w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></span>
                ) : (
                  <Trash2 size={16} />
                )}
              </button>
            )}
          </div>
        ))}
      </div> */}
	  {/* Categories */}
<div className="flex flex-wrap gap-3 mb-6">
  {categories.map((category, i) => (
    <button
      key={i}
      onClick={() => setActiveCategory(category)}
      className={`flex items-center gap-2 px-3 py-1 rounded font-medium transition-colors ${
        activeCategory === category
          ? "bg-blue-600 text-white"
          : "bg-gray-200 text-gray-800 hover:bg-gray-300"
      }`}
    >
      <span>{category}</span>

      {category !== "All" && (
        <span
          onClick={(e) => {
            e.stopPropagation(); // prevent selecting category
            handleDeleteCategory(category);
          }}
          className={`ml-1 text-red-600 hover:text-red-800 cursor-pointer ${
            deletingCategory === category ? "opacity-50" : ""
          }`}
        >
          {deletingCategory === category ? (
            <span className="inline-block w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></span>
          ) : (
            <Trash2 size={14} />
          )}
        </span>
      )}
    </button>
  ))}
</div>

      {/* Blog cards */}
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {blogs?.length > 0 ? (
          blogs.map((blog, index) => {
            const isLast = blogs.length === index + 1;
            return (
              <div
                ref={isLast ? lastBlogRef : null}
                key={blog.key}
                className="relative overflow-hidden bg-white rounded-lg shadow-xl"
              >
                {/* Action buttons */}
                <div className="absolute top-2 left-2 flex gap-2">
                  <button
                    onClick={() => openEditModal(blog)}
                    className="p-1 text-blue-600 bg-white rounded-full shadow hover:text-blue-800"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(blog)}
                    disabled={deletingBlogKey === blog.key}
                    className="p-1 text-red-600 bg-white rounded-full shadow hover:text-red-800 disabled:opacity-50"
                  >
                    {deletingBlogKey === blog.key ? (
                      <span className="inline-block w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      <Trash2 size={16} />
                    )}
                  </button>
                </div>

                {/* Thumbnail */}
                <img
                  src={
                    blog.thumbnail ||
                    "https://via.placeholder.com/400x200?text=No+Image"
                  }
                  className="object-cover w-full h-48"
                  alt={blog.title || "Blog Image"}
                />

                {/* Content */}
                <div className="p-6">
                  <h3 className="mb-2 text-lg font-semibold">
                    {blog.title || "Untitled"}
                  </h3>
                  <div
                    className="mb-4 text-gray-600 line-clamp-4"
                    dangerouslySetInnerHTML={{
                      __html:
                        blog.content?.substring(0, 50) +
                          (blog.content?.length > 50 ? "..." : "") || "",
                    }}
                  />
                  <button
                    onClick={() => setPreviewBlog(blog)}
                    className="flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    <Eye size={14} /> Read More
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <p className="text-gray-500">No blogs found.</p>
        )}
      </div>

      {/* Loader */}
      {loading && blogs.length > 0 && (
        <div className="flex justify-center mt-6">
          <LoaderSpiner />
        </div>
      )}

      {/* Preview Modal */}
      {previewBlog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="relative w-11/12 max-w-4xl p-6 bg-white rounded-lg shadow-lg overflow-y-auto max-h-[90vh]">
            <button
              onClick={() => setPreviewBlog(null)}
              className="absolute top-3 right-3 text-gray-600 hover:text-black"
            >
              ✖
            </button>
            <h2 className="mb-4 text-2xl font-bold">{previewBlog.title}</h2>
            <img
              src={
                previewBlog.thumbnail ||
                "https://via.placeholder.com/600x300?text=No+Image"
              }
              alt={previewBlog.title}
              className="w-full h-60 object-cover rounded mb-4"
            />
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: previewBlog.content }}
            />
          </div>
        </div>
      )}

      {/* Edit Modal (Compact Style) */}
      {editingBlog && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "12px",
              boxShadow: "0px 4px 15px rgba(0,0,0,0.2)",
              width: "420px",
              minHeight: "420px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                marginBottom: "15px",
                textAlign: "center",
              }}
            >
              Edit Blog
            </h2>

            {/* Title */}
            <input
              type="text"
              placeholder="Blog Title"
              style={{
                marginBottom: "10px",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                fontSize: "14px",
              }}
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
            />

            {/* Category Dropdown */}
            <select
              style={{
                marginBottom: "10px",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                fontSize: "14px",
              }}
              value={formData.category}
              onChange={(e) =>
                setFormData({ ...formData, category: e.target.value })
              }
            >
              <option value="">-- Select Category --</option>
              {categories
                .filter((cat) => cat !== "All") // exclude "All"
                .map((cat, i) => (
                  <option key={i} value={cat}>
                    {cat}
                  </option>
                ))}
              <option value="Other">Other</option>
            </select>

            {/* Custom Category */}
            {formData.category === "Other" && (
              <input
                type="text"
                placeholder="Enter custom category"
                style={{
                  marginBottom: "10px",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
                value={formData.customCategory || ""}
                onChange={(e) =>
                  setFormData({ ...formData, customCategory: e.target.value })
                }
              />
            )}

            {/* Thumbnail */}
            <input
              type="text"
              placeholder="Thumbnail URL"
              style={{
                marginBottom: "10px",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                fontSize: "14px",
              }}
              value={formData.thumbnail}
              onChange={(e) =>
                setFormData({ ...formData, thumbnail: e.target.value })
              }
            />
            {formData.thumbnail && (
              <img
                src={formData.thumbnail}
                alt="Thumbnail Preview"
                style={{
                  width: "100%",
                  height: "180px",
                  objectFit: "cover",
                  borderRadius: "6px",
                  marginBottom: "15px",
                }}
              />
            )}

            {/* Content */}
            <textarea
              placeholder="Content"
              style={{
                marginBottom: "10px",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                fontSize: "14px",
                minHeight: "100px",
              }}
              value={formData.content}
              onChange={(e) =>
                setFormData({ ...formData, content: e.target.value })
              }
            />

            {/* Buttons */}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={closeEditModal}
                disabled={editLoading}
                style={{
                  marginRight: "10px",
                  padding: "8px 14px",
                  backgroundColor: "#e5e7eb",
                  border: "none",
                  borderRadius: "6px",
                  cursor: editLoading ? "not-allowed" : "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>

              <button
                onClick={handleUpdateBlog}
                disabled={editLoading}
                style={{
                  padding: "8px 14px",
                  backgroundColor: editLoading ? "#9ca3af" : "#3b82f6",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: editLoading ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "120px",
                }}
              >
                {editLoading ? (
                  <span
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid #fff",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      display: "inline-block",
                      marginRight: "8px",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                ) : (
                  "Save"
                )}
                {editLoading ? " Saving..." : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPost;
